<template>
  <el-dialog
    :visible.sync="isVisible"
    title="Activity Log"
    width="60%"
    :before-close="handleClose"
    class="activity-log-dialog"
  >
    <div class="activity-log-header mb-3">
      <div class="header-left">
        <el-input
          v-model="searchQuery"
          placeholder="Search activities..."
          prefix-icon="el-icon-search"
          clearable
          size="small"
          style="width: 250px"
        />
        <el-select
          v-model="filterType"
          placeholder="Filter by type"
          clearable
          size="small"
          style="width: 200px"
        >
          <el-option
            v-for="type in activityTypeOptions"
            :key="type.value"
            :label="type.label"
            :value="type.value"
          />
        </el-select>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-refresh"
          :loading="loading"
          @click="refreshActivities"
        >
          Refresh
        </el-button>
      </div>
      <el-button size="small" type="danger" icon="el-icon-delete" @click="handleClearActivities">
        Clear All
      </el-button>
    </div>

    <div v-if="loading" class="activity-loading">
      <i class="el-icon-loading"></i>
      <span>Loading activities...</span>
    </div>

    <div v-else-if="filteredActivities.length === 0" class="activity-empty">
      <i class="el-icon-document"></i>
      <p>No activities found</p>
    </div>

    <div v-else class="activity-list">
      <el-timeline>
        <el-timeline-item
          v-for="activity in filteredActivities"
          :key="activity.timestamp + activity.type"
          :timestamp="formatTimestamp(activity.timestamp)"
          :type="getActivityColor(activity.type)"
          placement="top"
        >
          <el-card shadow="hover" class="activity-card">
            <div class="activity-content">
              <div class="activity-icon">
                <i :class="getActivityIcon(activity.type)"></i>
              </div>
              <div class="activity-details">
                <div class="activity-user">
                  <strong>{{ activity.userName || "Unknown User" }}</strong>
                  <span v-if="activity.userEmail" class="activity-email">
                    ({{ activity.userEmail }})
                  </span>
                </div>
                <div class="activity-description">
                  {{ getActivityDescription(activity) }}
                  <span v-if="activity.entityName" class="activity-entity">
                    - <em>{{ activity.entityName }}</em>
                  </span>
                </div>
                <div v-if="activity.details" class="activity-meta">
                  <el-tag
                    v-for="(value, key) in getDisplayableDetails(activity.details)"
                    :key="key"
                    size="mini"
                    type="info"
                    class="detail-tag"
                  >
                    {{ formatDetailKey(key) }}: {{ value }}
                  </el-tag>
                </div>
              </div>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ActivityDescriptions } from "@/api/activityResource";

export default {
  name: "ActivityLog",
  data() {
    return {
      isVisible: false,
      searchQuery: "",
      filterType: "",
    };
  },
  computed: {
    ...mapGetters("activity", ["activities", "loading"]),
    activityTypeOptions() {
      return Object.entries(ActivityDescriptions).map(([value, label]) => ({
        value,
        label: this.capitalizeWords(label),
      }));
    },
    filteredActivities() {
      let result = this.activities;

      if (this.filterType) {
        result = result.filter((a) => a.type === this.filterType);
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (a) =>
            (a.userName && a.userName.toLowerCase().includes(query)) ||
            (a.description && a.description.toLowerCase().includes(query)) ||
            (a.entityName && a.entityName.toLowerCase().includes(query)) ||
            (a.userEmail && a.userEmail.toLowerCase().includes(query))
        );
      }

      return result;
    },
  },
  methods: {
    ...mapActions("activity", ["fetchActivities", "clearActivities"]),

    show() {
      this.isVisible = true;
      this.refreshActivities();
    },

    hide() {
      this.isVisible = false;
    },

    handleClose(done) {
      this.searchQuery = "";
      this.filterType = "";
      done();
    },

    async refreshActivities() {
      await this.fetchActivities();
    },

    handleClearActivities() {
      this.$confirm("Are you sure you want to clear all activity logs?", "Warning", {
        confirmButtonText: "Yes, Clear All",
        cancelButtonText: "Cancel",
        type: "warning",
      })
        .then(() => {
          this.clearActivities();
          this.$message.success("Activity log cleared");
        })
        .catch(() => {});
    },

    getActivityDescription(activity) {
      // If we have a proper description, use it
      if (activity.description && activity.description !== activity.type) {
        return activity.description;
      }
      // Otherwise, look up from ActivityDescriptions
      return ActivityDescriptions[activity.type] || this.formatActivityType(activity.type);
    },

    formatActivityType(type) {
      if (!type) return "Unknown action";
      // Convert USER_UPDATED to "Updated user"
      return type
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "Unknown time";
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getActivityColor(type) {
      if (!type) return "info";
      if (type.includes("DELETE")) return "danger";
      if (type.includes("CREATE") || type.includes("UPLOAD")) return "success";
      if (type.includes("UPDATE") || type.includes("PUSH")) return "warning";
      if (type.includes("LOGIN")) return "success";
      if (type.includes("LOGOUT")) return "info";
      return "primary";
    },

    getActivityIcon(type) {
      if (!type) return "el-icon-info";
      if (type.includes("USER")) return "el-icon-user";
      if (type.includes("DASHBOARD")) return "el-icon-data-board";
      if (type.includes("DATA") || type.includes("FILE")) return "el-icon-document";
      if (type.includes("ROLE")) return "el-icon-s-custom";
      if (type.includes("LOGIN")) return "el-icon-right";
      if (type.includes("LOGOUT")) return "el-icon-switch-button";
      return "el-icon-info";
    },

    getDisplayableDetails(details) {
      if (!details || typeof details !== "object") return {};
      // Filter out internal/redundant fields
      const excludeKeys = ["userId", "targetUserId", "loginTime", "logoutTime"];
      return Object.fromEntries(
        Object.entries(details).filter(
          ([key, value]) => !excludeKeys.includes(key) && value !== null && value !== undefined
        )
      );
    },

    formatDetailKey(key) {
      return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },

    capitalizeWords(str) {
      return str.replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-log-dialog {
  ::v-deep .el-dialog__body {
    max-height: 60vh;
    overflow-y: auto;
  }
}

.activity-log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;

  .header-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
}

.activity-loading,
.activity-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 10px;
  }
}

.activity-list {
  padding-top: 20px;
}

.activity-card {
  margin-bottom: 0;

  ::v-deep .el-card__body {
    padding: 12px 15px;
  }
}

.activity-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.activity-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 18px;
    color: #606266;
  }
}

.activity-details {
  flex: 1;
}

.activity-user {
  font-size: 14px;
  margin-bottom: 4px;
}

.activity-email {
  color: #909399;
  font-size: 12px;
}

.activity-description {
  color: #606266;
  font-size: 13px;
  margin-bottom: 6px;
}

.activity-entity {
  color: #409eff;
}

.activity-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.detail-tag {
  font-size: 11px;
}
</style>
